import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload an image (JPG, PNG, GIF, WebP, SVG)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Upload failed');
      }

      const data = await res.json();
      const imageUrl = `${API_URL}${data.url}`;
      
      setPreview(imageUrl);
      onChange(imageUrl);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Upload area */}
      <div className="flex gap-4">
        {/* Preview / Upload Box */}
        <div 
          className={`relative w-32 h-32 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-all overflow-hidden ${
            uploading ? 'border-orange-400 bg-orange-50' : 
            preview ? 'border-gray-200 bg-gray-50' : 
            'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
          }`}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="image-upload-area"
        >
          {uploading ? (
            <div className="flex flex-col items-center text-orange-500">
              <Loader2 className="w-8 h-8 animate-spin mb-1" />
              <span className="text-xs">Uploading...</span>
            </div>
          ) : preview ? (
            <>
              <img 
                src={preview} 
                alt="Preview" 
                className="w-full h-full object-cover"
                onError={() => setPreview('')}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                data-testid="image-remove-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center text-gray-400">
              <Upload className="w-8 h-8 mb-1" />
              <span className="text-xs">Upload</span>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="image-file-input"
          />
        </div>

        {/* URL Input */}
        <div className="flex-1">
          <div className="text-xs text-gray-500 mb-2">Or paste image URL:</div>
          <input
            type="url"
            value={value || ''}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            data-testid="image-url-input"
          />
          <p className="text-xs text-gray-400 mt-2">
            Supported: JPG, PNG, GIF, WebP, SVG (max 5MB)
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1" data-testid="image-upload-error">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
